/* eslint-disable */
import { createTheme } from "@material-ui/core/styles";
const getMode = () => {
  if (typeof document !== "undefined" && document.documentElement) {
    return document.documentElement.getAttribute("data-mode") === "dark"
      ? "dark"
      : "light";
  }
  return "light";
};

const defaultTheme = createTheme({
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
  palette: {
    type: getMode(),
    // IMPORTANT: keep these as concrete hex values. MUI v4 feeds palette colors
    // through fade()/lighten()/darken()/emphasize() to derive hover, ripple,
    // outlined-border and Snackbar shades — all of which call decomposeColor()
    // and throw on a `var(...)` string ("Unsupported `var(--…)` color").
    // Theme-aware colors are applied below through component `overrides`, which
    // are emitted as raw CSS and can safely reference the CSS variables.
    primary: {
      main: "#337AB7",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#5A9BD5",
      contrastText: "#ffffff",
    },
    background: {
      default: "#F5F7FA",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1F2933",
      secondary: "#6B7280",
    },
  },

  overrides: {
    // In DARK mode only, repaint every Paper-based surface (Card, Dialog,
    // Menu, Popover, Drawer…) with the dark tokens. Scoped to
    // html[data-mode="dark"] so light mode is completely untouched.
    MuiPaper: {
      root: {
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--color-background-paper)",
          color: "var(--color-text-primary)",
        },
      },
      rounded: {
        borderRadius: 16,
      },
      elevation1: {
        'html[data-mode="dark"] &': {
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.45)",
        },
      },
    },

    // Tables: in dark mode, default cell text is MUI's hardcoded near-black,
    // which vanishes on a dark surface. Repaint cell text + the row dividers.
    // Scoped to dark mode, so light mode is untouched. Header cells that set
    // their own white text / dark bg keep working (light text on a dark header
    // reads fine, and !important rules elsewhere still win).
    MuiTableCell: {
      root: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
          borderBottomColor: "var(--total_row_boldbackground)",
        },
      },
    },

    MuiTableRow: {
      root: {
        'html[data-mode="dark"] &:hover': {
          backgroundColor: "rgba(255, 255, 255, 0.04)",
        },
      },
    },

    MuiButton: {
      containedPrimary: {
        backgroundColor: "var(--color-primary-main)",
        color: "var(--color-primary-contrast)",
        "&:hover": {
          backgroundColor: "var(--generic_icons_color)",
        },
      },
      outlinedPrimary: {
        borderColor: "var(--color-primary-main)",
        color: "var(--color-primary-main)",
        "&:hover": {
          backgroundColor: "color-mix(in srgb, var(--color-primary-main) 8%, transparent)",
          borderColor: "var(--color-primary-main)",
        },
      },
      textPrimary: {
        color: "var(--color-primary-main)",
        "&:hover": {
          backgroundColor: "color-mix(in srgb, var(--color-primary-main) 8%, transparent)",
        },
      },
    },

    MuiCircularProgress: {
      colorPrimary: {
        color: "var(--color-primary-main)",
      },
      colorSecondary: {
        color: "var(--color-secondary-main)",
      },
    },

    MuiCheckbox: {
      colorPrimary: {
        color: "var(--color-primary-main)",
        "&.Mui-checked": {
          color: "var(--color-primary-main)",
        },
      },
    },

    MuiRadio: {
      colorPrimary: {
        color: "var(--color-primary-main)",
        "&.Mui-checked": {
          color: "var(--color-primary-main)",
        },
      },
    },

    MuiSwitch: {
      colorPrimary: {
        "& .MuiSwitch-thumb": {
          backgroundColor: "var(--color-primary-main)",
        },
        "& .Mui-checked + .MuiSwitch-track": {
          backgroundColor: "var(--color-primary-main)",
        },
      },
    },

    MuiTabs: {
      indicator: {
        backgroundColor: "var(--color-primary-main)",
      },
    },

    MuiTab: {
      textColorPrimary: {
        "&.Mui-selected": {
          color: "var(--color-primary-main)",
        },
      },
    },

    MuiLinearProgress: {
      barColorPrimary: {
        backgroundColor: "var(--color-primary-main)",
      },
    },

    MuiChip: {
      colorPrimary: {
        backgroundColor: "var(--color-primary-main)",
        color: "var(--color-primary-contrast)",
      },
      colorSecondary: {
        backgroundColor: "var(--color-secondary-main)",
        color: "var(--color-secondary-contrast)",
      },
    },

    MuiLink: {
      root: {
        color: "var(--color-primary-main)",
      },
    },
    MuiTablePagination: {
      root: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
      selectIcon: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
      actions: {
        "& button": {
          color: "var(--color-text-primary)",
          opacity: 1,
        },
        "& button.Mui-disabled": {
          color: "var(--color-text-primary)",
          opacity: 0.4,
        },
        "& button svg": {
          fill: "currentColor",
        },
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
          "& button": {
            color: "var(--color-text-primary)",
            opacity: 1,
          },
          "& button.Mui-disabled": {
            color: "var(--color-text-primary)",
            opacity: 0.4,
          },
        },
      },
      caption: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
      selectLabel: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
      displayedRows: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
      select: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiSelect: {
      root: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
      icon: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
      select: {
        "&:focus": {
          backgroundColor: "transparent",
        },
      },
    },
    MuiAutocomplete: {
      popupIndicator: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
      clearIndicator: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiInputBase: {
      root: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
      input: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
          "&::placeholder": {
            color: "var(--color-text-secondary)",
            opacity: 0.7,
          },
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        'html[data-mode="dark"] & fieldset': {
          borderColor: "var(--total_row_boldbackground)",
        },
        'html[data-mode="dark"] &:hover:not(.Mui-disabled) fieldset': {
          borderColor: "var(--color-text-secondary)",
        },
        "&.Mui-focused fieldset": {
          borderColor: "var(--color-primary-main) !important",
        },
      },
    },

    MuiFormLabel: {
      root: {
        "&.Mui-focused": {
          color: "var(--color-primary-main) !important",
        },
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
          "&.Mui-focused": {
            color: "var(--color-primary-main)",
          },
        },
      },
    },
    MuiInput: {
      underline: {
        'html[data-mode="dark"] &:before': {
          borderBottomColor: "var(--total_row_boldbackground)",
        },
        'html[data-mode="dark"] &:hover:not(.Mui-disabled):before': {
          borderBottomColor: "var(--color-primary-main)",
        },
        'html[data-mode="dark"] &:after': {
          borderBottomColor: "var(--color-primary-main)",
        },
      },
    },
    MuiListItemIcon: {
      root: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
    },
    MuiDivider: {
      root: {
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--total_row_boldbackground)",
          opacity: 0.2,
        },
      },
    },
    MuiMenu: {
      paper: {
        borderRadius: 12,
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--color-background-paper)",
          color: "var(--color-text-primary)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        },
      },
    },
    MuiMenuItem: {
      root: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
          "&:hover": {
            backgroundColor: "var(--total_row_background)",
          },
          "&.Mui-selected": {
            backgroundColor: "var(--total_row_boldbackground)",
            "&:hover": {
              backgroundColor: "var(--total_row_boldbackground)",
            },
          },
        },
      },
    },
    MuiAlert: {
      root: {
        borderRadius: 12,
        fontWeight: 500,
      },
      filledSuccess: {
        backgroundColor: "var(--color-primary-main)",
        color: "var(--color-primary-contrast)",
      },
      filledError: {
        backgroundColor: "var(--error-color)",
        color: "#ffffff",
      },
      filledInfo: {
        backgroundColor: "var(--color-secondary-main)",
        color: "var(--color-secondary-contrast)",
      },
      filledWarning: {
        backgroundColor: "#f59e0b",
        color: "#ffffff",
      },
    },
    MuiSnackbarContent: {
      root: {
        borderRadius: 12,
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--color-background-paper)",
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 16,
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--color-background-paper)",
          color: "var(--color-text-primary)",
          boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
        },
      },
    },
    MuiDialogTitle: {
      root: {
        padding: "20px 24px",
        "& .MuiTypography-root": {
          fontSize: "1.125rem",
          fontWeight: 800,
          'html[data-mode="dark"] &': {
             color: "var(--color-text-primary)",
          }
        },
      },
    },
    MuiDialogContent: {
      root: {
        padding: "8px 24px 24px 24px",
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
    },
    MuiDialogActions: {
      root: {
        padding: "16px 24px",
        borderTop: "1px solid var(--total_row_boldbackground)",
        "& .MuiButton-text": {
          color: "var(--color-primary-main)",
        },
      },
    },
    MuiStepIcon: {
      root: {
        color: "var(--total_row_boldbackground)",
      },
      active: {
        color: "var(--color-primary-main)",
      },
      completed: {
        color: "var(--color-primary-main)",
      },
    },
    MuiStepConnector: {
      line: {
        borderColor: "var(--total_row_boldbackground)",
      },
      active: {
        "& .MuiStepConnector-line": {
          borderColor: "var(--color-primary-main)",
        },
      },
      completed: {
        "& .MuiStepConnector-line": {
          borderColor: "var(--color-primary-main)",
        },
      },
    },
    MuiPickersBasePicker: {
      pickerView: {
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--color-background-paper)",
        },
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
      dayLabel: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
        },
      },
      iconButton: {
        'html[data-mode="dark"] &': {
          backgroundColor: "transparent",
          color: "var(--color-text-primary)",
        },
      },
    },
    MuiPickersDay: {
      day: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-primary)",
        },
      },
      dayDisabled: {
        'html[data-mode="dark"] &': {
          color: "var(--color-text-secondary)",
          opacity: 0.4,
        },
      },
      daySelected: {
        backgroundColor: "var(--color-primary-main)",
        color: "var(--color-primary-contrast)",
        "&:hover": {
          backgroundColor: "var(--generic_icons_color)",
        },
      },
      current: {
        color: "var(--color-primary-main)",
      },
    },
    MuiPickersModal: {
      dialogRoot: {
        'html[data-mode="dark"] &': {
          backgroundColor: "var(--color-background-paper)",
        },
      },
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: "var(--color-primary-main)",
        "& h4, & h8, & span, & button, & h3": {
          color: "var(--color-primary-contrast) !important",
        },
      },
    },
  },
});

export default defaultTheme;
